/**
 * NexusCanvas — Phase 4 force-directed 2D canvas.
 *
 * Pure HTML5 canvas physics simulation. Verlet-style: repulsion + spring
 * edges + centre attraction. Settles in ~150 ticks then idles unless the
 * parent bumps `resetSignal`. Theme-aware (light/dark).
 *
 * Interactions:
 *  • drag-to-pan (empty space)
 *  • scroll-to-zoom (centred on cursor)
 *  • drag-to-pin a node (sticks where dropped)
 *  • hover dims the rest of the graph
 *  • click → onSelect(id) (or onMultiSelect when multiSelectMode is on)
 */
import React, { useEffect, useRef } from 'react';

export function NexusCanvas({
  graph, onSelect, selectedId, multiSelectMode, multiSelectIds, onMultiSelect,
  heatmapOn, heatmap, timeYear, isDark, resetSignal,
  newEdgeKeys,             // Set<string> of `src|dst|kind` strings paint with marching-ants
  newEdgeBornAt = 0,       // Date.now() when the new edges were added; used for fade
}) {
  /* eslint-disable -- useRef containers for physics state + DOM overlay handles intentionally
     trip the v6 react-hooks/refs and immutability rules. Both rules don't exist in the runtime
     CRA eslint v5 plugin; the patterns here are standard imperative graphics code. */
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);              // wrapper that holds per-node test handles
  const overlayHandlesRef = useRef(new Map());  // nodeId → HTMLDivElement (1×1 invisible)
  const animationRef = useRef(null);
  // Stale-callback refs so the overlay DOM handlers always see the latest
  // multi-select mode + on-select handlers without rebinding listeners.
  const handlersRef = useRef({ onSelect, onMultiSelect, multiSelectMode });
  handlersRef.current = { onSelect, onMultiSelect, multiSelectMode };
  /* eslint-enable */
  const stateRef = useRef({
    nodes: [], edges: [], adj: {}, hoveredId: null, paused: false,
    pan: { x: 0, y: 0 }, scale: 1,
    dragNode: null, dragOffset: { x: 0, y: 0 },
    panning: false, lastMouse: { x: 0, y: 0 },
  });

  // ── Build initial physics state when graph changes ──
  useEffect(() => {
    if (!graph || !graph.nodes) return;
    const cw = containerRef.current?.clientWidth || 1200;
    const ch = containerRef.current?.clientHeight || 700;
    const cx = cw / 2;
    const cy = ch / 2;
    const connectedIds = new Set();
    graph.edges.forEach((e) => { connectedIds.add(e.src); connectedIds.add(e.dst); });
    const visibleNodes = graph.nodes.filter((n) => connectedIds.has(n.id));
    const KIND_ANGLES = {
      investor:        -Math.PI / 2,
      startup:         Math.PI / 6,
      person:          5 * Math.PI / 6,
      holding:         -5 * Math.PI / 6,
      subsidiary:      -Math.PI / 6,
      government:      -Math.PI / 3,
      university:      Math.PI / 3,
      rd_center:       2 * Math.PI / 3,
      service_provider: -2 * Math.PI / 3,
    };
    const physicsNodes = visibleNodes.map((n) => {
      const angle = KIND_ANGLES[n.kind] !== undefined ? KIND_ANGLES[n.kind] : (Math.random() * 2 - 1) * Math.PI;
      const r = 180 + Math.random() * 80;
      return {
        ...n,
        x: cx + Math.cos(angle) * r + (Math.random() - 0.5) * 40,
        y: cy + Math.sin(angle) * r + (Math.random() - 0.5) * 40,
        vx: 0, vy: 0,
        radius: 7 + Math.min(10, (n.degree || 0) * 0.7),
        pinned: false,
      };
    });
    const adj = {};
    graph.edges.forEach((e) => {
      adj[e.src] = adj[e.src] || new Set();
      adj[e.dst] = adj[e.dst] || new Set();
      adj[e.src].add(e.dst);
      adj[e.dst].add(e.src);
    });
    stateRef.current.nodes = physicsNodes;
    stateRef.current.edges = graph.edges.filter((e) => connectedIds.has(e.src) && connectedIds.has(e.dst));
    stateRef.current.adj = adj;
    stateRef.current.tick = 0;

    // ── Sync DOM overlay handles to match the current node set ──
    // Each handle is an invisible 14×14 div that carries `data-testid` so
    // E2E tests can click on a known node by id instead of guessing canvas
    // pixel positions. Positions are updated imperatively in draw().
    const overlay = overlayRef.current;
    const handles = overlayHandlesRef.current;
    if (overlay) {
      const liveIds = new Set(physicsNodes.map((n) => n.id));
      // Drop any handles whose node no longer exists in this graph
      for (const [id, el] of handles.entries()) {
        if (!liveIds.has(id)) { el.remove(); handles.delete(id); }
      }
      // Create handles for any new nodes
      physicsNodes.forEach((n) => {
        if (handles.has(n.id)) return;
        const el = document.createElement('div');
        el.setAttribute('data-testid', `shura-node-${n.id}`);
        el.setAttribute('data-node-id', n.id);
        el.setAttribute('data-node-kind', n.kind);
        el.setAttribute('data-node-label', n.label);
        el.style.cssText = 'position:absolute;width:14px;height:14px;border-radius:50%;cursor:pointer;transform:translate(-50%,-50%);background:transparent;z-index:2;pointer-events:auto;';
        el.addEventListener('click', (ev) => {
          ev.stopPropagation();
          const h = handlersRef.current;
          if (h.multiSelectMode) h.onMultiSelect(n.id);
          else h.onSelect(n.id);
        });
        overlay.appendChild(el);
        handles.set(n.id, el);
      });
    }
  }, [graph]);

  // ── Reset view (pan + zoom + node pins + restart sim) when parent bumps signal ──
  useEffect(() => {
    if (resetSignal === undefined) return;
    stateRef.current.pan = { x: 0, y: 0 };
    stateRef.current.scale = 1;
    stateRef.current.tick = 0;
    stateRef.current.nodes.forEach((n) => { n.pinned = false; n.vx = 0; n.vy = 0; });
  }, [resetSignal]);

  // ── Animation loop ──
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      canvas.width = w * window.devicePixelRatio;
      canvas.height = h * window.devicePixelRatio;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(window.devicePixelRatio, 0, 0, window.devicePixelRatio, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);

    const REPULSION = 2200;
    const SPRING_K = 0.018;
    const SPRING_LEN = 90;
    const CENTRE_K = 0.0028;
    const DAMPING = 0.82;
    const MAX_TICKS_BEFORE_FREEZE = 400;

    const draw = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      const cx = w / 2;
      const cy = h / 2;
      const { nodes, edges, adj, hoveredId, pan, scale } = stateRef.current;
      stateRef.current.tick = (stateRef.current.tick || 0) + 1;

      if (!stateRef.current.paused && stateRef.current.tick < MAX_TICKS_BEFORE_FREEZE) {
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const a = nodes[i]; const b = nodes[j];
            const dx = b.x - a.x; const dy = b.y - a.y;
            const dist = Math.max(20, Math.sqrt(dx * dx + dy * dy));
            const force = REPULSION / (dist * dist);
            const fx = (dx / dist) * force;
            const fy = (dy / dist) * force;
            if (!a.pinned) { a.vx -= fx; a.vy -= fy; }
            if (!b.pinned) { b.vx += fx; b.vy += fy; }
          }
        }
        edges.forEach((e) => {
          const a = nodes.find((n) => n.id === e.src);
          const b = nodes.find((n) => n.id === e.dst);
          if (!a || !b) return;
          const dx = b.x - a.x; const dy = b.y - a.y;
          const dist = Math.max(1, Math.sqrt(dx * dx + dy * dy));
          const force = SPRING_K * (dist - SPRING_LEN);
          const fx = (dx / dist) * force;
          const fy = (dy / dist) * force;
          if (!a.pinned) { a.vx += fx; a.vy += fy; }
          if (!b.pinned) { b.vx -= fx; b.vy -= fy; }
        });
        nodes.forEach((n) => {
          if (n.pinned) return;
          n.vx += (cx - n.x) * CENTRE_K;
          n.vy += (cy - n.y) * CENTRE_K;
        });
        nodes.forEach((n) => {
          if (n.pinned) { n.vx = 0; n.vy = 0; return; }
          n.vx *= DAMPING; n.vy *= DAMPING;
          n.x += n.vx; n.y += n.vy;
          n.x = Math.max(40, Math.min(w - 40, n.x));
          n.y = Math.max(40, Math.min(h - 40, n.y));
        });
      }

      // ── Render ──
      ctx.clearRect(0, 0, w, h);
      ctx.save();
      ctx.translate(pan.x, pan.y);
      ctx.scale(scale, scale);

      // Heatmap radial pulse (sector circles) — drawn under the graph
      if (heatmapOn && heatmap && heatmap.sectors && heatmap.sectors.length) {
        const palette = ['#8B3FC5', '#7C3AED', '#10B981', '#0EA5E9', '#3B82F6', '#06B6D4', '#A78BFA', '#EF4444'];
        const ringTime = Date.now() / 1000;
        heatmap.sectors.slice(0, 6).forEach((s, i) => {
          const sectorNodes = nodes.filter((n) => (n.meta?.sector || 'Other') === s.sector);
          if (sectorNodes.length === 0) return;
          const ax = sectorNodes.reduce((acc, n) => acc + n.x, 0) / sectorNodes.length;
          const ay = sectorNodes.reduce((acc, n) => acc + n.y, 0) / sectorNodes.length;
          const baseR = 60 + Math.min(160, s.nodes * 6);
          const pulse = (Math.sin(ringTime * 1.4 + i) + 1) * 0.5;
          const colour = palette[i % palette.length];
          const grad = ctx.createRadialGradient(ax, ay, 8, ax, ay, baseR);
          grad.addColorStop(0, `${colour}44`);
          grad.addColorStop(0.6, `${colour}11`);
          grad.addColorStop(1, `${colour}00`);
          ctx.fillStyle = grad;
          ctx.globalAlpha = 0.55 + pulse * 0.35;
          ctx.beginPath();
          ctx.arc(ax, ay, baseR, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.globalAlpha = 1;
      }

      // Time travel — fade edges whose created_at year > slider
      const filterEdge = (e) => {
        if (!e.created_at) return true;
        const y = new Date(e.created_at).getFullYear();
        return y <= timeYear;
      };

      const dimMode = hoveredId !== null;
      const hoverNeighbours = hoveredId ? adj[hoveredId] || new Set() : null;
      const baseEdgeAlpha   = isDark ? 0.45 : 0.65;
      const dimAdjAlpha     = isDark ? 0.95 : 0.95;
      const dimOtherAlpha   = isDark ? 0.06 : 0.10;
      const outOfTimeAlpha  = isDark ? 0.05 : 0.07;
      // v168 — Live-paint animation: edges in `newEdgeKeys` get a
      // violet marching-ants dash + glow for 10s after they were
      // added. The dashOffset is tied to wall-clock so re-renders
      // don't reset the march.
      const nowMs = Date.now();
      const ageMs = nowMs - (newEdgeBornAt || 0);
      const showNewEdgeFx = newEdgeKeys && newEdgeKeys.size > 0 && ageMs < 10_000;
      const newEdgeFade = showNewEdgeFx ? Math.max(0, 1 - (ageMs / 10_000)) : 0;
      const dashOffset = (nowMs / 30) % 24; // marches ~33fps
      edges.forEach((e) => {
        const a = nodes.find((n) => n.id === e.src);
        const b = nodes.find((n) => n.id === e.dst);
        if (!a || !b) return;
        const inTime = filterEdge(e);
        const isAdjacent = hoveredId && (e.src === hoveredId || e.dst === hoveredId);
        const edgeKey = `${e.src}|${e.dst}|${e.kind}`;
        const isNew = showNewEdgeFx && newEdgeKeys.has(edgeKey);
        if (isNew) {
          // Violet glow underlay
          ctx.save();
          ctx.shadowColor = '#AE52EA';
          ctx.shadowBlur = 16 * newEdgeFade + 6;
          ctx.strokeStyle = '#AE52EA';
          ctx.globalAlpha = 0.9;
          ctx.lineWidth = 2.4;
          ctx.setLineDash([10, 6]);
          ctx.lineDashOffset = -dashOffset;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          ctx.restore();
          // Skip the normal stroke below since we've already drawn it
          return;
        }
        ctx.strokeStyle = (!isDark && e.color && e.color.toUpperCase() === '#FFFFFF') ? '#0A0A0A' : e.color;
        ctx.globalAlpha = !inTime ? outOfTimeAlpha : (dimMode ? (isAdjacent ? dimAdjAlpha : dimOtherAlpha) : baseEdgeAlpha);
        ctx.lineWidth = Math.min(3, 0.6 + (e.weight || 1) * 0.4) * (isAdjacent ? 1.7 : 1);
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      });
      nodes.forEach((n) => {
        const isHovered = n.id === hoveredId;
        const isAdjacent = hoverNeighbours && hoverNeighbours.has(n.id);
        const isSelected = n.id === selectedId;
        const inMulti = multiSelectIds.includes(n.id);
        const safeAccent = (!isDark && n.accent && n.accent.toUpperCase() === '#FFFFFF') ? '#0A0A0A' : n.accent;
        ctx.globalAlpha = dimMode && !isHovered && !isAdjacent ? 0.18 : 1;
        if (isHovered || isSelected || inMulti) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.radius + 8, 0, Math.PI * 2);
          ctx.fillStyle = safeAccent + '30';
          ctx.fill();
        }
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.radius, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? '#0F1A2D' : '#FFFFFF';
        ctx.fill();
        ctx.lineWidth = inMulti ? 3 : isSelected ? 2.5 : (n.pinned ? 2 : 1.5);
        ctx.strokeStyle = inMulti ? (isDark ? '#C77CFF' : '#5E1F8B') : safeAccent;
        ctx.stroke();
        if (n.pinned) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = isDark ? '#C77CFF' : '#5E1F8B';
          ctx.fill();
        }
        if (isHovered || isSelected || inMulti || (n.degree || 0) > 4) {
          ctx.fillStyle = isDark ? '#FFFFFF' : '#0A0A0A';
          ctx.font = '11px ui-sans-serif, system-ui';
          ctx.textAlign = 'center';
          ctx.fillText(n.label.slice(0, 22), n.x, n.y + n.radius + 14);
        }
      });
      ctx.globalAlpha = 1;
      ctx.restore();

      // ── Reposition the invisible DOM overlay handles for E2E targeting ──
      // Done OUTSIDE the canvas transform so we use screen pixels directly.
      // pan + scale match the world→screen mapping used by the canvas ctx.
      const handles = overlayHandlesRef.current;
      if (handles.size) {
        nodes.forEach((n) => {
          const el = handles.get(n.id);
          if (!el) return;
          const sx = pan.x + n.x * scale;
          const sy = pan.y + n.y * scale;
          el.style.left = `${sx}px`;
          el.style.top  = `${sy}px`;
        });
      }

      animationRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resize);
    };
    // eslint-disable-next-line
  }, [selectedId, multiSelectIds, heatmapOn, heatmap, timeYear, isDark]);

  // ── Mouse handlers — pan + zoom + node drag ──
  const screenToWorld = (mx, my) => {
    const { pan, scale } = stateRef.current;
    return { x: (mx - pan.x) / scale, y: (my - pan.y) / scale };
  };
  const findNodeAt = (mx, my) => {
    const w = screenToWorld(mx, my);
    return stateRef.current.nodes.find((n) => {
      const dx = n.x - w.x; const dy = n.y - w.y;
      return Math.sqrt(dx * dx + dy * dy) <= n.radius + 4;
    });
  };

  const handleMouseDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const hit = findNodeAt(mx, my);
    if (hit) {
      const w = screenToWorld(mx, my);
      stateRef.current.dragNode = hit;
      stateRef.current.dragOffset = { x: hit.x - w.x, y: hit.y - w.y };
    } else {
      stateRef.current.panning = true;
      stateRef.current.lastMouse = { x: mx, y: my };
    }
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const drag = stateRef.current.dragNode;
    if (drag) {
      const w = screenToWorld(mx, my);
      drag.x = w.x + stateRef.current.dragOffset.x;
      drag.y = w.y + stateRef.current.dragOffset.y;
      drag.vx = 0; drag.vy = 0;
      return;
    }
    if (stateRef.current.panning) {
      const last = stateRef.current.lastMouse;
      stateRef.current.pan.x += (mx - last.x);
      stateRef.current.pan.y += (my - last.y);
      stateRef.current.lastMouse = { x: mx, y: my };
      return;
    }
    const hit = findNodeAt(mx, my);
    stateRef.current.hoveredId = hit ? hit.id : null;
    canvasRef.current.style.cursor = hit ? 'pointer' : (stateRef.current.panning ? 'grabbing' : 'grab');
  };

  const handleMouseUp = () => {
    if (stateRef.current.dragNode) {
      stateRef.current.dragNode.pinned = true;
      stateRef.current.dragNode = null;
    }
    stateRef.current.panning = false;
  };

  const handleClick = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const hit = findNodeAt(mx, my);
    if (!hit) return;
    if (multiSelectMode) onMultiSelect(hit.id);
    else onSelect(hit.id);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const oldScale = stateRef.current.scale;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const newScale = Math.max(0.4, Math.min(3, oldScale * factor));
    const w = screenToWorld(mx, my);
    stateRef.current.scale = newScale;
    stateRef.current.pan.x = mx - w.x * newScale;
    stateRef.current.pan.y = my - w.y * newScale;
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full rounded-2xl overflow-hidden ${isDark ? 'bg-[#050810]' : 'bg-white border border-slate-200'}`}
    >
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { stateRef.current.hoveredId = null; stateRef.current.panning = false; stateRef.current.dragNode = null; }}
        onClick={handleClick}
        onWheel={handleWheel}
        data-testid="shura-canvas"
      />
      {/* Invisible DOM handles per node — for deterministic E2E testing.
          Each child <div> carries data-testid="shura-node-{id}" and forwards
          clicks to onSelect / onMultiSelect. Positioned imperatively each
          frame via draw(). */}
      <div
        ref={overlayRef}
        data-testid="shura-node-overlay"
        className="absolute inset-0 pointer-events-none"
        style={{ touchAction: 'none' }}
      >
        {/* children appended imperatively; each child has pointer-events:auto via cursor:pointer */}
      </div>
      <div className={`absolute top-3 left-3 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] rounded-lg backdrop-blur pointer-events-none ${
        isDark
          ? 'bg-[#050810]/80 border border-white/10 text-slate-300'
          : 'bg-white/85 border border-slate-200 text-slate-600'
      }`}>
        Drag to pan · scroll to zoom · drag node to pin
      </div>
    </div>
  );
}
